import type {
  DirectionCandidate,
  ProjectState,
  PromptMode,
} from "./types";
import { SKILL_CATALOG } from "./skill-catalog";

const value = (input: string | undefined, fallback = "尚未确定") =>
  input?.trim() || fallback;

const bullets = (items: string[]) =>
  items.length ? items.map((item) => `- ${item}`).join("\n") : "- 尚未记录";

function selectedDirection(state: ProjectState): DirectionCandidate | undefined {
  return state.directions.find(
    (direction) => direction.id === state.selectedDirectionId,
  );
}

function venueSection(state: ProjectState) {
  const facts = state.venueBrief?.facts ?? [];
  return `## Venue 与投稿边界

- 会议/期刊：${value(state.venueBrief?.venueName)}
- 赛道：${value(state.venueBrief?.track)}
- 来源 URL：
${state.venueUrls
  .split("\n")
  .filter(Boolean)
  .map((url) => `  - ${url}`)
  .join("\n") || "  - 尚未提供"}

已核验要求：
${facts.length ? facts.map((fact) => `- ${fact.label}: ${fact.value} [${fact.evidenceUrl}]`).join("\n") : "- 尚未核验。必须先访问来源，不得凭记忆补全。"}

未知项：
${bullets(state.venueBrief?.unknowns ?? [])}

把上述要求作为研究和交付约束自然落实。不要把审稿标准或征稿措辞生硬写入论文正文。`;
}

function profileSection(state: ProjectState) {
  return `## 研究者画像

- 领域：${value(state.profile.domain)}
- 经验：${value(state.profile.experience)}
- 长处：${value(state.profile.strengths)}
- 可复用资产：${value(state.profile.priorAssets)}
- 应避免的方向/重叠：${value(state.profile.avoidTopics)}
- 算力：${value(state.profile.compute)}
- 模型：${value(state.profile.modelAccess)}
- 数据：${value(state.profile.dataAccess)}
- 预算：${value(state.profile.budget)}
- 截止日期：${value(state.profile.deadline)}
- 风险偏好：${state.profile.riskTolerance}

优先选择能放大已有长处、同时在期限和资源内产生可审计证据的问题。`;
}

function directionSection(state: ProjectState) {
  const direction = selectedDirection(state);
  if (!direction) {
    return `## 研究方向

尚未冻结方向。先完成系统性文献与工程调研，提出 3 个实质不同的候选方向，再用新颖性、可实现性、证据可得性、与既有工作的差异及失败风险比较。`;
  }
  return `## 已选研究方向

- 标题：${direction.title}
- 一句话：${direction.oneLine}
- 研究问题：${direction.researchQuestion}
- 未解决缺口：${direction.unresolvedGap}
- 核心机制：${direction.proposedMechanism}
- Benchmark 计划：${direction.benchmarkPlan}
- 主指标：${direction.primaryMetric}
- 基线：${direction.baselines.join("；")}
- 风险：
${bullets(direction.risks)}
- 转向信号：${direction.pivotSignal}

方向被冻结后，除非触发转向条件，不得仅因单次结果不理想而改写研究问题。`;
}

function experimentSection(state: ProjectState) {
  const experiment = state.experiment;
  return `## 实验契约

- 可证伪假设：${value(experiment.hypothesis)}
- 任务定义：${value(experiment.taskDefinition)}
- 实现栈与约束：${value(experiment.implementationStack)}
- 运行矩阵：${value(experiment.runMatrix)}
- 数据集与切分：${value(experiment.datasets)}
- 评测协议：${value(experiment.evaluationProtocol)}
- 主指标：${value(experiment.primaryMetric)}
- 次要指标：${value(experiment.secondaryMetrics)}
- 基线：${value(experiment.baselines)}
- 主实验比较：${value(experiment.mainComparison)}
- 消融：${value(experiment.ablations)}
- 超参：${value(experiment.hyperparameters)}
- 鲁棒性：${value(experiment.robustness)}
- Case study：${value(experiment.caseStudy)}
- 随机性控制：${value(experiment.seeds)}
- 资源预算：${value(experiment.computeBudget)}
- Artifact 计划：${value(experiment.artifactPlan)}
- 报告与绘图计划：${value(experiment.reportingPlan)}
- 停止条件：${value(experiment.stopCriteria)}
- 转向条件：${value(experiment.pivotCriteria)}

实验必须遵守：
1. 先冻结任务定义、切分、主指标和选表规则，再运行主比较。
2. 至少覆盖 main、机制相关消融、鲁棒性、效率/成本和代表性案例；超参分析只服务于核心主张。
3. 主结果与 post-hoc replay/修复严格分开。不得用后验规则替换首次冻结结果。
4. 原始逐题/逐样本记录不可手工修改。图表只能从冻结的 processed 文件生成。
5. 失败运行、无增益消融和中止原因保留在日志中。正文可以聚焦核心证据，但不能伪造或篡改结果。
6. 只有同数据、同切分、同指标、同评测协议可比时，才使用 SOTA 表述；否则改写为明确的应用场景、约束或权衡优势。`;
}

function paperSection(state: ProjectState) {
  return `## 论文叙事与图表

- 暂定标题：${value(state.paper.workingTitle)}
- 核心主张：${value(state.paper.centralClaim)}
- 贡献：${value(state.paper.contributions)}
- 叙事主线：${value(state.paper.narrative)}
- 章节：${state.paper.sections}
- 引用规则：${state.paper.citationRules}
- 写作约束：${state.paper.writingConstraints}

图表要求：
${state.paper.figures
  .map(
    (figure, index) => `${index + 1}. ${figure.role}
   - 核心信息：${figure.message}
   - 证据边界：${figure.evidence}
   - 交付：${figure.format}`,
  )
  .join("\n")}

绘图阶段优先调用 Codex 中已安装的 \`${state.paper.figureSkill}\`。调用前完整阅读其 SKILL.md，并遵守其 editable source、导出、截图和视觉 QA 流程。若技能不可用，先报告缺失能力，不要把不可编辑的临时图冒充最终稿。

写作采用 claim–evidence ledger。摘要、引言和结论只强化已经被结果或引用支持的核心记忆点。参考文献必须能在论文、出版社、标准组织或权威索引中核验；禁止 mock 引用。`;
}

function skillsSection(state: ProjectState, allowlist?: string[]) {
  const selected = SKILL_CATALOG.filter(
    (skill) =>
      state.selectedSkills.includes(skill.id) &&
      (!allowlist || allowlist.includes(skill.id)),
  );
  return `## 专业技能路由

开始执行前，检查以下技能是否已安装。对每个可用技能，必须先完整阅读其
\`SKILL.md\`，再按对应工作流操作；缺失时使用 \`$skill-installer\` 搜索同名技能，
或报告缺失并采用最接近的可审计替代流程。不得假装已经调用不存在的技能。

${selected.length
  ? selected
      .map(
        (skill) =>
          `- \`$${skill.name}\`：${skill.description} 使用时机：${skill.useWhen}${
            skill.bundled ? "（随 ScholarFlow Skills Pack 提供）" : ""
          }`,
      )
      .join("\n")
  : "- 尚未选择技能。执行前至少配置文献检索、绘图、LaTeX/PDF QA 与审稿能力。"}

技能只负责增强流程，不能越过用户授权、证据边界或目录约束。`;
}

function referencePapersSection(state: ProjectState) {
  if (!state.referencePapers.length) return "";
  return `## 用户导入的参考论文

${state.referencePapers
  .map(
    (paper) =>
      `- ${paper.name}（${paper.pageCount} 页，已抽取 ${paper.charCount.toLocaleString()} 字符）`,
  )
  .join("\n")}

这些论文只作为问题定位、协议设计和引用核验的线索。正式写作前必须重新核对
论文原文与可检索元数据，不得把其结论或数值写成当前项目的实验结果。`;
}

function appendixSection(state: ProjectState, stages: Array<keyof typeof state.stageNotes>) {
  const entries = stages
    .map((stage) => [stage, state.stageNotes[stage]?.trim()] as const)
    .filter((entry): entry is readonly [typeof entry[0], string] => Boolean(entry[1]));
  if (!entries.length) return "";
  return `## 用户自由附录

以下内容来自用户在表单之外补充的阶段备注。它们具有与结构化字段同等的约束效力；
若与已冻结协议冲突，先向用户指出冲突，不得静默覆盖。

${entries
  .map(([stage, text]) => `### ${stage}\n\n${text}`)
  .join("\n\n")}`;
}

function workspaceSection(state: ProjectState) {
  return `## 工作目录契约

项目根目录：${value(state.workspace.absolutePath, "由用户在 ScholarFlow 中绑定并粘贴绝对路径")}

只能在项目根目录内工作，保留阶段轨迹：

\`\`\`text
00_admin/
  project_brief.md
  status.md
  ai_logs/conversations.jsonl
  memory/context_capsules.jsonl
  decisions/decision_ledger.md
01_venue/
  requirements.md
02_direction/
  decision_record.md
03_experiments/
  experiment_contract.md
  code/
  configs/
  runs/
  results/raw/
  results/processed/
  results/figures/
  logs/
04_paper/
  main.tex
  references.bib
  claim_evidence_ledger.csv
  sections/
  tables/
  figures/briefs/
  figures/drafts/
  figures/editable/
  figures/final/
  figures/qa/
05_review/
  rounds/
  action_tracker.md
06_submission/
  paper_source/
  artifact/
  checklist.md
  openreview_fields.md
prompts/
  master_prompt.md
  generated/
\`\`\`

不要删除或覆盖用户已有结果。批量实验和格式化可产生派生文件，但每个关键阶段都要更新 status、decision ledger 和输入/输出清单。`;
}

function memorySection(state: ProjectState) {
  const capsules = state.memory.capsules.slice(-4);
  return `## 稳定记忆与上下文纪律

活动上下文预算约 ${state.memory.contextBudget.toLocaleString()} tokens。不要把全部网页、日志和历史对话反复送入模型。使用三层记忆：

1. 原始层：网页、论文、运行记录和完整 AI 对话，保存在固定文件中，仅按需读取。
2. 证据层：来源索引、claim–evidence ledger、实验 manifest 与结果表。
3. 活动层：最近的上下文胶囊，仅包含决定、证据、约束、未决问题和文件引用。

当前胶囊：
${capsules.length ? capsules.map((capsule) => `- ${capsule.title}: ${capsule.summary}`).join("\n") : "- 尚无胶囊。完成一个阶段后生成。"}

压缩时不能把推断升级成事实，不能丢失负面结果、协议变更、失败原因与 post-hoc 标记。`;
}

const commonRole = `你是一名能够使用本地文件、浏览器、代码运行环境、文献检索、
LaTeX 和已安装技能的高级科研智能体。所有结论必须能回到来源、冻结协议或本地产物。
不伪造引用、实验或 venue 要求，不通过修改数据迎合论文故事。`;

const commonAuthorization = `任何会公开仓库、上传文件、提交论文、改变权限或覆盖用户
已有文件的操作，都必须先确认授权范围。API key 不得进入日志、提示词或交付包。`;

function masterPrompt(state: ProjectState) {
  return `# Codex 学术论文全流程任务

${commonRole}

## 目标与阶段纪律

完成 venue 核验、研究者画像、文献调研、方向比较、实验、论文、图表、评审与投稿交付。
一次只推进一个阶段；每阶段开始时声明输入与验收闸门，结束时写入可检查产物和 status。

## 初始研究设想

${value(state.idea)}

${venueSection(state)}

${profileSection(state)}

${referencePapersSection(state)}

${directionSection(state)}

${experimentSection(state)}

${paperSection(state)}

${appendixSection(state, ["venue", "profile", "direction", "experiment", "paper", "prompt", "workspace"])}

${skillsSection(state)}

${workspaceSection(state)}

${memorySection(state)}

## 全流程执行协议

1. 读取根目录、AGENTS.md、status、decision ledger 与已有证据，先判断项目真实状态。
2. 核验 venue 与最接近工作，形成来源表、未知项和候选方向，不从热门模型反推问题。
3. 冻结任务、切分、指标、基线、预算、选表、停止与转向规则后再运行主实验。
4. 原始运行、派生结果、绘图输入和论文主张分层保存；main 与 post-hoc 严格分离。
5. 写作前建立 claim–evidence ledger，图表前建立 brief，修改前建立 action tracker。
6. 每阶段更新 \`00_admin/status.md\`，写明完成项、证据路径、风险与下一步。

## 最终验收

- 研究问题、机制和证据形成一条可复述的因果链。
- 每项贡献有对应实验、引用或明确限制。
- 代码、配置、原始记录、表格和图可以从冻结输入重建。
- PDF、源码、artifact、投稿字段、身份策略和哈希彼此一致。

${commonAuthorization}`;
}

function landscapePrompt(state: ProjectState) {
  return `# Codex 调研与选题任务

${commonRole}

## 本阶段唯一目标

从 venue 约束、研究者长处和真实文献中冻结一个重要、可实现、可证伪的问题—机制组合。
本阶段不运行大规模实验，不写完整论文，不承诺 SOTA。

## 输入

### 初始设想

${value(state.idea)}

${venueSection(state)}

${profileSection(state)}

${referencePapersSection(state)}

${appendixSection(state, ["venue", "profile", "direction"])}

${skillsSection(state, [
  "nature-academic-search",
  "expand-references",
  "trace-citations",
  "nature-reader",
  "scholarflow-research-orchestrator",
  "scholarflow-evidence-audit",
])}

## 执行步骤

1. 从官方页面提取主题、track、篇幅、时间、匿名、artifact 与 AI disclosure；事实和未知项分开。
2. 将用户能力拆成领域知识、工程资产、数据权限、算力、预算、期限和不可重叠工作。
3. 以 1–3 篇种子论文扩展基础、近邻、桥接、近期和综述文献；逐条核验题名、作者、年份与来源。
4. 为最接近工作建立比较表：问题、输入、输出、允许信息、机制、数据、切分、指标、成本与局限。
5. 提出 3 个实质不同的候选方向。每个方向说明重要性、缺口、机制、可用数据、强基线、预期证据和失败模式。
6. 用新颖性、可实现性、证据准备度、资源匹配和投稿相关性评分，拒绝只换框架名的伪创新。
7. 冻结一个方向，同时记录转向信号和仍需小规模预试验验证的关键假设。

## 必须交付

- \`01_venue/requirements.md\`：来源、访问日期、事实、未知项与风险。
- 文献来源表与最接近工作矩阵，引用均可检索。
- 三张方向卡与一份 \`02_direction/decision_record.md\`。
- 冻结的研究问题、核心机制、候选 benchmark、主指标、基线族与 pivot signal。

## 通过闸门

只有当问题能被非本方向读者理解、与最近工作差异明确、数据和指标可获得、
资源预算可承受且至少一个实验能证伪核心机制时，才能进入实验阶段。

${commonAuthorization}`;
}

function experimentPrompt(state: ProjectState) {
  return `# Codex 实验执行任务

${commonRole}

## 本阶段唯一目标

只实现和验证已经冻结的核心机制。无关的 venue 背景、章节写作和投稿格式不参与执行。

${directionSection(state)}

## 可用资源边界

- 算力：${value(state.profile.compute)}
- 模型/API：${value(state.profile.modelAccess)}
- 数据权限：${value(state.profile.dataAccess)}
- 预算与期限：${value(state.profile.budget)}；${value(state.profile.deadline)}

${experimentSection(state)}

${referencePapersSection(state)}

${appendixSection(state, ["experiment"])}

${skillsSection(state, [
  "nature-reader",
  "nature-figure",
  "scholarflow-research-orchestrator",
  "scholarflow-evidence-audit",
])}

## 执行步骤

1. 在代码前复述输入、输出、允许信息、切分、主指标、风险指标和公平比较条件。
2. 生成冻结配置与 run matrix；每个 run 记录代码版本、配置哈希、数据版本、seed、模型版本、时间和成本。
3. 先做最小可行性与泄漏检查。若协议错误，修订必须写入 decision ledger，并重新冻结版本。
4. 在同模型、同数据、同切分、同预算、同评测器条件下运行强基线与完整方法。
5. 消融一次只删除一个可解释机制；鲁棒性扰动必须对应真实部署风险；效率和成本使用同一计量口径。
6. 原始逐样本记录写入 \`results/raw/\` 且不手工修改；汇总、置信区间和图表只由脚本生成。
7. 对异常高分、任务饱和、失败重试和 post-hoc repair 单独审计，不能混入首次冻结主结果。
8. 按停止条件决定收敛、缩小主张或触发 pivot，不因追求漂亮数字无限调参。

## 必须交付

- \`03_experiments/experiment_contract.md\` 与冻结 configs。
- 可运行代码、环境锁文件、run manifests、原始记录、processed CSV/JSON 与绘图脚本。
- 主实验、机制消融、鲁棒性、效率/成本、超参和案例结果；只保留能回答主张的正文候选。
- 一份 error taxonomy、失败运行日志和结论边界说明。

## 通过闸门

主指标与风险指标共同支持核心主张；增益能由机制解释；公平性和泄漏检查通过；
原始记录、配置和图表输入完整可追溯。否则不得开始撰写结果性结论。

${commonAuthorization}`;
}

function paperPrompt(state: ProjectState) {
  return `# Codex 论文写作任务

${commonRole}

## 本阶段唯一目标

只依据已冻结引用和实验产物组织论文论证。这里不重新设计实验，不从写作需要反推结果。

${directionSection(state)}

${experimentSection(state)}

${paperSection(state)}

${appendixSection(state, ["paper", "prompt"])}

${skillsSection(state, [
  "researchwrite",
  "nature-academic-search",
  "trace-citations",
  "sci-diagram-drawing",
  "nature-figure",
  "latex:latex-compile",
  "pdf:pdf",
  "scholarflow-evidence-audit",
])}

## 执行步骤

1. 扫描冻结结果、图表输入和真实引用，建立 claim–evidence ledger；无证据主张先删除或降级。
2. 把核心故事压缩成：重要问题、现有缺口、独特解法、关键证据、实际意义。
3. 为每节写论证合同：本节问题、主张、证据、与前后节的逻辑接口，再写段落。
4. 摘要和引言以问题与结果开场；相关工作按研究分歧组织；方法从形式化、总览和机制展开。
5. 实验部分围绕研究问题组织主表、消融、鲁棒性、效率和案例，明确可比条件与限制。
6. 每句话通常挂 1–2 个直接支持引用；BibTeX 逐条核验，不使用 mock 条目。
7. 图、表、公式和 PDF 每次结构性修改后编译并视觉检查；正文只呈现最必要的证据。

## 必须交付

- \`04_paper/claim_evidence_ledger.csv\` 与章节论证合同。
- 完整 TeX、真实 \`references.bib\`、表格源码、图表 briefs/editable/final/qa。
- 编译 PDF 和逐页视觉 QA 记录。

## 通过闸门

标题、摘要、引言、方法、实验与结论围绕同一核心贡献；所有强主张可追溯；
优势条件被解释；限制准确；语言自然克制且没有项目汇报式叙事。

${commonAuthorization}`;
}

function figurePrompt(state: ProjectState) {
  return `# Codex 学术图表任务

${commonRole}

## 本阶段唯一目标

只规划、绘制和验收论文图表。不要修改实验数字、论文主张或方法模块来迁就版式。

- 核心主张：${value(state.paper.centralClaim)}
- 叙事主线：${value(state.paper.narrative)}

${state.paper.figures
  .map(
    (figure, index) => `## Figure ${index + 1}: ${figure.role}

- 读者要理解的核心信息：${figure.message}
- 证据边界：${figure.evidence}
- 交付格式：${figure.format}`,
  )
  .join("\n\n")}

${appendixSection(state, ["paper"])}

${skillsSection(state, [
  "sci-diagram-drawing",
  "nature-figure",
  "pdf:pdf",
  "scholarflow-evidence-audit",
])}

## 执行步骤与交付

1. 每张图先写 brief：读者问题、视觉层级、必须出现的元素、可删除元素和禁止暗示的结论。
2. 框架图必须让模块与方法小节、消融组件一一对应；实验图只能读取冻结 processed 文件。
3. 使用共享绘图技能生成 editable source，再导出 PDF 与 PNG；不以不可编辑位图充当最终源。
4. 检查字号、线宽、色盲可读性、半栏/通栏缩放、图例、标签、留白、裁切和 PDF 嵌入效果。
5. 保存 brief、草稿、editable、final 与 QA 截图到 \`04_paper/figures/\` 对应目录。

## 通过闸门

缩放到论文实际尺寸仍可读；任何视觉优势都有数据支撑；图注可独立理解；
源文件可编辑、导出可复现且已完成视觉验收。

${commonAuthorization}`;
}

function reviewPrompt(state: ProjectState) {
  return `# Codex 独立评审与修改任务

${commonRole}

## 本阶段唯一目标

从审稿人视角发现会影响正确性、可信度和理解成本的问题，再以最小改动修复。
不为了回应所有意见而破坏主线，也不主动编造审稿人未提出的负面结论。

- 核心主张：${value(state.paper.centralClaim)}
- 贡献：${value(state.paper.contributions)}
- 叙事主线：${value(state.paper.narrative)}
- 评审策略：${value(state.paper.reviewRounds)}

${appendixSection(state, ["paper"])}

${skillsSection(state, [
  "nature-reviewer",
  "nature-response",
  "scholarflow-evidence-audit",
  "pdf:pdf",
])}

## 执行步骤

1. 独立检查 originality、soundness、可比性、复现性、主张边界、引用和表达，不读取先前模拟评分。
2. 把问题分为阻断、重要、可选；多人共识、事实错误、协议不公平和主张越界优先。
3. 每项意见记录定位、证据、修改动作、是否采纳和理由；拒绝不适用意见时给出具体依据。
4. 修改后重新编译、视觉检查，并核对摘要、主表、结论与 claim–evidence ledger 是否同步。
5. 不用“虽然没有超过”“遗憾的是”等措辞替审稿人攻击自己；用准确定位和适用条件化解。

## 必须交付

- \`05_review/rounds/round-N/\` 下的独立报告、修改前后版本与证据。
- \`05_review/action_tracker.md\`：共识、严重性、动作、状态和理由。
- 修订后 PDF 与剩余风险清单。

## 通过闸门

阻断问题清零；重要意见均有证据化处理；论文逻辑没有因局部修补断裂；
核心主张仍由同一组冻结证据承担。

${commonAuthorization}`;
}

function submissionPrompt(state: ProjectState) {
  return `# Codex 投稿交付任务

${commonRole}

## 本阶段唯一目标

只处理最终格式、身份策略、表单、代码包和一致性核验。不得改写论文科学内容或实验数字。

${venueSection(state)}

## 论文元数据

- 标题：${value(state.paper.workingTitle)}
- 核心主张：${value(state.paper.centralClaim)}
- 章节：${value(state.paper.sections)}

${appendixSection(state, ["venue", "paper", "workspace"])}

${skillsSection(state, [
  "latex:latex-doctor",
  "latex:latex-compile",
  "pdf:pdf",
  "github:yeet",
  "scholarflow-submission-qa",
])}

${workspaceSection(state)}

## 执行步骤

1. 重新访问官方投稿页面，核验 track、deadline、页数、模板、匿名/单盲、作者元数据、AI disclosure、artifact 与许可证。
2. 从最终 source 目录干净编译；逐页检查页眉、作者、图表、公式、参考文献、链接、页数、裁切和字体。
3. PDF、TeX、README、代码仓库、artifact 和投稿表单中的标题、摘要、作者、关键词与链接逐项一致。
4. 从当前未压缩目录重新打包源码与代码；排除 API key、本机路径、缓存、私有数据和无关历史。
5. 列出 ZIP 内容，计算 SHA-256，填写提交清单和表单草稿；未经授权不执行最终提交。

## 必须交付

- \`06_submission/paper_source/\`、\`artifact/\`、\`checklist.md\` 和投稿字段草稿。
- 最终 PDF、源码 ZIP、代码 ZIP、文件清单与哈希。
- 身份/匿名、页数、链接、AI disclosure 和可复现性逐项证据。

## 通过闸门

所有官方要求有来源；PDF 无视觉缺陷；包内无秘密和私有路径；
各交付物元数据一致，且最终提交动作仍等待用户明确授权。

${commonAuthorization}`;
}

export function buildMasterPrompt(
  state: ProjectState,
  mode: PromptMode = state.promptMode,
) {
  const builders: Record<PromptMode, (project: ProjectState) => string> = {
    master: masterPrompt,
    landscape: landscapePrompt,
    experiment: experimentPrompt,
    paper: paperPrompt,
    figure: figurePrompt,
    review: reviewPrompt,
    submission: submissionPrompt,
  };
  return builders[mode](state).replace(/\n{3,}/g, "\n\n").trim() + "\n";
}

export function estimatePromptTokens(prompt: string) {
  const cjk = (prompt.match(/[\u3400-\u9fff]/g) ?? []).length;
  return Math.ceil(cjk + (prompt.length - cjk) / 4);
}

export function estimateProjectTokens(state: ProjectState) {
  const text = JSON.stringify(state);
  const cjk = (text.match(/[\u3400-\u9fff]/g) ?? []).length;
  return Math.ceil(cjk + (text.length - cjk) / 4);
}

export function buildCompressionPayload(
  state: ProjectState,
  recentLogs: Array<{ task: string; response: unknown }>,
) {
  return {
    project: {
      projectName: state.projectName,
      idea: state.idea,
      venueBrief: state.venueBrief,
      profile: state.profile,
      selectedDirection: selectedDirection(state),
      experiment: state.experiment,
      paper: state.paper,
      workspace: {
        absolutePath: state.workspace.absolutePath,
        checks: state.workspace.report?.checks,
      },
    },
    priorCapsules: state.memory.capsules.slice(-3),
    recentInteractions: recentLogs.slice(-8),
  };
}
